import { fromScaled, toScaled } from '@colplat/protocol';
import { byId, groupBy, type Ledger, type SplitRow } from './rows.js';

/*
 * The names here are `Account*` rather than plain `Register*` because
 * `register.ts` already owns those for the WALLET register, and the two are
 * genuinely different shapes — one row per transaction with a movement per
 * currency here, one row per money leg there.
 */

/**
 * One account's register — legacy's `ledger` action, ported out of SQL so it
 * runs wherever the rows are (ADR-0025).
 *
 * The difference between this and the reports is the grain and the running
 * balance. A report answers "what happened"; a register answers "what did
 * *this* account do, and what was it worth after each one" — so rows
 * accumulate strictly forward in time from an opening balance, and one row is
 * one *transaction*, not one leg.
 *
 * That last point is easy to get wrong and legacy is explicit about it: a
 * single transaction can touch the same account twice — an in-account
 * currency exchange — so legs are grouped by transaction and each row carries
 * a movement per currency.
 */

export interface AccountRegisterFilters {
  from?: string;
  to?: string;
  /** A split-level currency filter — the only place in the app that filters on a *leg's* currency. */
  currency?: string;
}

export interface AccountMovement {
  currencyCode: string;
  amount: string;
  running: string;
}

export interface AccountRegisterRow {
  transactionId: string;
  txDate: string;
  description: string;
  reference: string;
  memo: string;
  transfer: string;
  transferAccountId: string | null;
  movements: AccountMovement[];
  version: number;
  seq: number | null;
}

export interface AccountRegisterLeg {
  accountId: string;
  accountName: string;
  memo: string;
  value: string;
  amount: string;
  currencyCode: string;
}

export interface AccountRegisterResult {
  opening: Record<string, string>;
  currencies: string[];
  balances: Record<string, string>;
  rows: AccountRegisterRow[];
  splitsByTx: Record<string, AccountRegisterLeg[]>;
}

export function buildAccountRegister(
  ledger: Ledger, accountId: string, filters: AccountRegisterFilters = {},
): AccountRegisterResult {
  const accounts = byId(ledger.accounts());
  const transactions = byId(ledger.transactions());
  const mine = ledger.splits().filter((s) => s.accountId === accountId && transactions.has(s.transactionId));

  /*
   * The account's balance is every leg it ever had, unfiltered.
   *
   * Deliberately not narrowed by the date range or the currency filter: this
   * is what the account is worth, which is not a question the filter bar gets
   * to change. The filter decides which rows are LISTED.
   */
  const balanceTotals = new Map<string, bigint>();
  for (const leg of mine) {
    balanceTotals.set(leg.currencyCode, (balanceTotals.get(leg.currencyCode) ?? 0n) + toScaled(leg.amount));
  }
  const balances: Record<string, string> = {};
  for (const [code, total] of balanceTotals) balances[code] = fromScaled(total);

  /*
   * Everything before the window, so the first listed row continues from a
   * real figure rather than from zero.
   *
   * Only when a start date is given: with no `from` the register begins at the
   * beginning and the opening balance IS zero.
   */
  const opening: Record<string, string> = {};
  if (filters.from) {
    const before = new Map<string, bigint>();
    for (const leg of mine) {
      if (filters.currency && leg.currencyCode !== filters.currency) continue;
      if (transactions.get(leg.transactionId)!.txDate >= filters.from) continue;
      before.set(leg.currencyCode, (before.get(leg.currencyCode) ?? 0n) + toScaled(leg.amount));
    }
    for (const [code, total] of before) opening[code] = fromScaled(total);
  }

  const listed = mine.filter((leg) => {
    const tx = transactions.get(leg.transactionId)!;
    if (filters.from && tx.txDate < filters.from) return false;
    if (filters.to && tx.txDate > filters.to) return false;
    if (filters.currency && leg.currencyCode !== filters.currency) return false;
    return true;
  });

  // `seq` decides order within a day — it is what a manual reorder rewrites.
  // The transaction id only breaks ties between imported rows sharing one,
  // and the split id makes the per-leg iteration deterministic.
  listed.sort((a, b) => {
    const ta = transactions.get(a.transactionId)!;
    const tb = transactions.get(b.transactionId)!;
    return ta.txDate.localeCompare(tb.txDate)
      || (ta.seq ?? 0) - (tb.seq ?? 0)
      || ta.id.localeCompare(tb.id)
      || a.id.localeCompare(b.id);
  });

  // Running balances accumulate from the opening figure, strictly forward.
  const running = new Map<string, bigint>();
  for (const [code, value] of Object.entries(opening)) running.set(code, toScaled(value));

  const byTx = new Map<string, AccountRegisterRow & { movementIndex: Map<string, AccountMovement> }>();
  const order: string[] = [];
  const memosByTx = new Map<string, Set<string>>();

  for (const leg of listed) {
    const tx = transactions.get(leg.transactionId)!;
    let row = byTx.get(leg.transactionId);
    if (!row) {
      row = {
        transactionId: leg.transactionId, txDate: tx.txDate,
        description: tx.description ?? '', reference: tx.reference ?? '',
        memo: '', transfer: '', transferAccountId: null, movements: [],
        version: tx.version ?? 0, seq: tx.seq ?? null,
        movementIndex: new Map(),
      };
      byTx.set(leg.transactionId, row);
      order.push(leg.transactionId);
      memosByTx.set(leg.transactionId, new Set());
    }
    if (leg.memo) memosByTx.get(leg.transactionId)!.add(leg.memo);

    running.set(leg.currencyCode, (running.get(leg.currencyCode) ?? 0n) + toScaled(leg.amount));
    const movement = row.movementIndex.get(leg.currencyCode);
    if (movement) {
      movement.amount = fromScaled(toScaled(movement.amount) + toScaled(leg.amount));
      movement.running = fromScaled(running.get(leg.currencyCode)!);
    } else {
      const fresh: AccountMovement = {
        currencyCode: leg.currencyCode, amount: leg.amount,
        running: fromScaled(running.get(leg.currencyCode)!),
      };
      row.movementIndex.set(leg.currencyCode, fresh);
      row.movements.push(fresh);
    }
  }

  // Counter accounts: every leg of the listed transactions except this
  // account's own.
  const legsByTx = groupBy(ledger.splits(), (s) => s.transactionId);
  for (const id of order) {
    const row = byTx.get(id)!;
    const others = new Map<string, string>();
    for (const leg of legsByTx.get(id) ?? []) {
      if (leg.accountId === accountId) continue;
      others.set(leg.accountId, accounts.get(leg.accountId)?.name ?? '?');
    }
    const names = [...others.values()];
    if (names.length > 1) {
      row.transfer = '-- Split --';
    } else if (names.length === 1) {
      row.transfer = names[0]!;
      row.transferAccountId = [...others.keys()][0]!;
    } else {
      // No counter account at all: money moved between currencies inside this
      // same account, which is the only way that happens.
      row.transfer = row.movements.length > 1 ? 'exchange' : '';
    }
    row.memo = [...memosByTx.get(id)!].join('; ');
    row.movements.sort((a, b) => a.currencyCode.localeCompare(b.currencyCode));
  }

  // Accumulated oldest-first, returned newest-first — the order a register is
  // read in. The opening row therefore belongs at the *bottom*.
  const rows = order.map((id) => {
    const { movementIndex, ...row } = byTx.get(id)!;
    void movementIndex;
    return row;
  }).reverse();

  // Every leg of every listed transaction, for the expandable split view.
  const splitsByTx: Record<string, AccountRegisterLeg[]> = {};
  for (const id of order) {
    splitsByTx[id] = [...(legsByTx.get(id) ?? [])]
      .sort((a: SplitRow, b: SplitRow) => a.id.localeCompare(b.id))
      .map((leg) => ({
        accountId: leg.accountId,
        accountName: accounts.get(leg.accountId)?.name ?? '?',
        memo: leg.memo ?? '',
        value: leg.value,
        amount: leg.amount,
        currencyCode: leg.currencyCode,
      }));
  }

  return {
    opening,
    currencies: Object.keys(balances).sort(),
    balances,
    rows,
    splitsByTx,
  };
}
